import {
  Logger,
  sortCustomFields,
  type CustomFieldData,
  type CustomFieldRemoveInput,
  type CustomTableType,
} from '@terros/common'
import { useQueryClient } from '@tanstack/react-query'
import { customFieldKey, customFieldsKey } from './keys'

const logger = new Logger('customFieldCache')
// logger.level = 'verbose'

type CustomFieldCache = {
  setItem: (customField: CustomFieldData) => void
  clearItem: (input: CustomFieldRemoveInput) => void
  clearList: VoidFunction
  setList: (tableType: CustomTableType, showArchived: boolean, customFields: CustomFieldData[]) => void
}

export function useCustomFieldCache(): CustomFieldCache {
  const queryClient = useQueryClient()

  const updateListQueries = (
    customField: CustomFieldData,
    callback: (existing: CustomFieldData[] | undefined) => CustomFieldData[] | undefined
  ): void => {
    queryClient.setQueriesData<CustomFieldData[]>(
      {
        queryKey: customFieldsKey(customField.tableType, { showArchived: customField.isDeleted ?? false }),
      },
      callback
    )
  }

  return {
    setItem: (customField: CustomFieldData) => {
      logger.verbose('set item', customField)
      queryClient.setQueryData<CustomFieldData>(customFieldKey(customField.fieldId), customField)
      updateListQueries(customField, (existing) => {
        if (!existing) return existing
        const updated = existing.map((cf) => (cf.fieldId !== customField.fieldId ? cf : customField))
        return sortCustomFields(updated)
      })
    },
    clearItem: (input: CustomFieldRemoveInput) => {
      queryClient.setQueriesData<CustomFieldData[]>(
        {
          queryKey: customFieldsKey(),
        },
        (existing) => {
          if (!existing) return []
          return sortCustomFields(existing.filter((cf) => cf.fieldId !== input.fieldId))
        }
      )
    },

    clearList: () => {
      queryClient.invalidateQueries({ queryKey: customFieldsKey() })
    },
    setList: (tableType: CustomTableType, showArchived: boolean, customFields: CustomFieldData[]) => {
      customFields.forEach((customField) => {
        queryClient.setQueryData<CustomFieldData>(customFieldKey(customField.fieldId), customField)
      })

      queryClient.setQueryData<CustomFieldData[]>(customFieldsKey(tableType, { showArchived }), (existing) => {
        if (!existing) return sortCustomFields(customFields)

        const fieldIdSet = new Set(customFields.map((cf) => cf.fieldId))
        const unchanged = existing.filter((cf) => !fieldIdSet.has(cf.fieldId))
        const merged = [...unchanged, ...customFields]

        return sortCustomFields(merged)
      })
    },
  }
}
